import { z } from "zod";
import { dodItemSchema, uuidSchema } from "./aiTypes.ts";
import { fileChangeSummarySchema } from "./changesets.ts";
import { request } from "./defs.ts";
import { projectIdSchema } from "./schemas.ts";

export const environmentTypeSchema = z.enum(["host", "worktree"]);
export type EnvironmentType = z.infer<typeof environmentTypeSchema>;

export const environmentStateSchema = z.enum([
  "creating",
  "active",
  "completed",
  "applied",
  "discarded",
  "failed",
]);
export type EnvironmentState = z.infer<typeof environmentStateSchema>;

export const executionEnvironmentSchema = z.strictObject({
  id: uuidSchema,
  type: environmentTypeSchema,
  machineId: uuidSchema,
  projectId: projectIdSchema,
  baseCommit: z.string().regex(/^[0-9a-f]{7,40}$/i),
  state: environmentStateSchema,
  runId: uuidSchema.nullable().optional(),
  changeSetId: uuidSchema.nullable().optional(),
  error: z.string().max(500).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExecutionEnvironment = z.infer<typeof executionEnvironmentSchema>;

export const gitStatusCheckSchema = z.strictObject({
  isGit: z.boolean(),
  hasHead: z.boolean(),
  isClean: z.boolean(),
  headCommit: z.string().regex(/^[0-9a-f]{7,40}$/i).optional(),
  reason: z.string().optional(),
  changedCount: z.number().int().nonnegative().optional(),
  changedFiles: z.array(z.string()).optional(),
  untrackedCount: z.number().int().nonnegative().optional(),
  modifiedCount: z.number().int().nonnegative().optional(),
});
export type GitStatusCheck = z.infer<typeof gitStatusCheckSchema>;

export const environmentRequests = {
  "system/project.dod": request(
    "system",
    "project.dod",
    z.strictObject({ projectId: projectIdSchema }),
    z.strictObject({ items: z.array(dodItemSchema) }),
  ),
  "system/git.status": request(
    "system",
    "git.status",
    z.strictObject({ projectId: projectIdSchema }),
    gitStatusCheckSchema,
  ),
  "agent/environment.create": request(
    "agent",
    "environment.create",
    z.strictObject({
      environmentId: uuidSchema,
      projectId: projectIdSchema,
      baseCommit: z.string().regex(/^[0-9a-f]{7,40}$/i),
    }),
    z.strictObject({
      ok: z.boolean(),
      environmentId: uuidSchema,
      baseCommit: z.string(),
    }),
  ),
  "agent/environment.discard": request(
    "agent",
    "environment.discard",
    z.strictObject({
      environmentId: uuidSchema,
      projectId: projectIdSchema,
    }),
    z.strictObject({ ok: z.boolean() }),
  ),
  "agent/environment.delta": request(
    "agent",
    "environment.delta",
    z.strictObject({
      environmentId: uuidSchema,
      projectId: projectIdSchema,
      baseCommit: z.string().regex(/^[0-9a-f]{7,40}$/i),
      runId: uuidSchema,
      sessionId: uuidSchema,
    }),
    z.strictObject({
      ok: z.boolean(),
      files: z.array(fileChangeSummarySchema),
      changeSetId: uuidSchema.nullable(),
      contentHash: z.string().nullable(),
      unsupportedPaths: z.array(z.strictObject({ path: z.string(), reason: z.string() })).optional(),
    }),
  ),
} as const;
